import React from 'react';
import { Calculator, BadgeDollarSign, Landmark, CalendarDays, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета налога
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Определение типа объекта (автомобиль или недвижимость)
  const isCarTax = 'carPower' in results;

  return (
    <ToolResult title="Результаты расчета налога">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Сумма налога */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Calculator className="w-4 h-4"/>
            {isCarTax ? 'Транспортный налог' : 'Налог на имущество'}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.totalTax, 0)}
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              за {results.year} год
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {isCarTax 
              ? `Ставка ${results.taxPerHorsePower} ₽/л.с. × ${results.carPower} л.с. × коэффициенты` 
              : `Ставка ${results.taxRate}% × кадастровая стоимость × коэффициенты`}
          </p>
        </div>

        {/* Базовый налог и повышающие коэффициенты */}
        <div className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 p-5 border border-amber-100 dark:border-amber-800/50 shadow-sm">
          <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
            <BadgeDollarSign className="w-4 h-4"/>
            {isCarTax 
              ? 'Повышающие коэффициенты' 
              : 'Налоговые коэффициенты'}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
              {formatCurrency(results.baseTax, 0)}
            </span>
            <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
              базовый налог
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            {isCarTax 
              ? (results.luxuryCoefficient > 1 
                  ? `Применен повышающий коэффициент ${results.luxuryCoefficient} (налог на роскошь)` 
                  : 'Без повышающих коэффициентов')
              : (results.propertyType === 'apartment' && results.cadastralValue >= 300000000 
                  ? 'Применен повышенный налог на роскошную недвижимость (2%)' 
                  : `Регион: ${results.region}, Тип: ${results.propertyType}`)
            }
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        {isCarTax ? (
          // Результаты для автомобиля
          <>
            <ResultItem 
              label="Автомобиль" 
              value={`${results.carPower} л.с., стоимость ${formatCurrency(results.carCost, 0)}`} 
              color="indigo" 
              icon={<Calculator className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Базовая ставка" 
              value={`${formatNumber(results.taxPerHorsePower, 0)} ₽ за 1 л.с.`} 
              color="amber" 
            />
            
            <ResultItem 
              label="Базовый налог" 
              value={formatCurrency(results.baseTax, 0)} 
            />
            
            {results.luxuryCoefficient > 1 && (
              <ResultItem 
                label="Повышающий коэффициент" 
                value={`${results.luxuryCoefficient} (налог на роскошь)`} 
                color="red" 
              />
            )}
            
            {results.electricCarCoefficient < 1 && (
              <ResultItem 
                label="Льгота для электромобиля" 
                value={results.electricCarCoefficient === 0 
                  ? 'Полное освобождение от налога' 
                  : `Скидка ${(1 - results.electricCarCoefficient) * 100}%`} 
                color="green" 
              />
            )}
            
            {results.registrationCoefficient > 1 && (
              <ResultItem 
                label="Коэффициент для импортных авто" 
                value={results.registrationCoefficient} 
                color="orange" 
              />
            )}
            
            {results.privilegeDiscount > 0 && (
              <ResultItem 
                label="Налоговая льгота" 
                value={`Скидка ${results.privilegeDiscount}%`} 
                color="green" 
              />
            )}
          </>
        ) : (
          // Результаты для недвижимости
          <>
            <ResultItem 
              label="Объект недвижимости" 
              value={`${results.propertyType === 'apartment' ? 'Квартира' : 
                     results.propertyType === 'house' ? 'Жилой дом' : 
                     results.propertyType === 'land' ? 'Земельный участок' : 
                     'Коммерческая недвижимость'}, ${results.propertyArea} м²`} 
              color="indigo" 
              icon={<Landmark className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Кадастровая стоимость" 
              value={formatCurrency(results.cadastralValue, 0)} 
              color="amber" 
            />
            
            <ResultItem 
              label="Налоговая база" 
              value={formatCurrency(results.taxableValue, 0)} 
            />
            
            <ResultItem 
              label="Базовая ставка налога" 
              value={`${results.taxRate}%`} 
              color={results.taxRate >= 2 ? 'red' : 'amber'} 
            />
            
            {results.regionalCoefficient !== 1 && (
              <ResultItem 
                label="Региональный коэффициент" 
                value={results.regionalCoefficient} 
                color="blue" 
              />
            )}
            
            {results.districtCoefficient !== 1 && (
              <ResultItem 
                label="Районный коэффициент" 
                value={results.districtCoefficient} 
                color="blue" 
              />
            )}
            
            {results.ownershipCoefficient !== 1 && (
              <ResultItem 
                label="Коэффициент доли в собственности" 
                value={formatNumber(results.ownershipCoefficient * 100, 0) + '%'} 
                color="purple" 
              />
            )}
            
            {results.privilegeDiscount > 0 && (
              <ResultItem 
                label="Налоговая льгота" 
                value={`Скидка ${results.privilegeDiscount}%`} 
                color="green" 
              />
            )}
          </>
        )}
        
        <ResultItem 
          label="Налоговый период" 
          value={results.year} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Итоговая сумма налога" 
          value={formatCurrency(results.totalTax, 0)} 
          color="indigo" 
          bold={true}
        />
      </div>

      {/* Информация о сроках уплаты налога */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Сроки уплаты налога</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isCarTax 
                ? 'Транспортный налог необходимо уплатить не позднее 1 декабря года, следующего за налоговым периодом.'
                : 'Налог на имущество физических лиц необходимо уплатить не позднее 1 декабря года, следующего за налоговым периодом.'}
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;